// FR-5 MVP accompaniment: fixed up/down arpeggio over chord symbols (or default I-IV-V-I).
// No AI chord inference (R-1 post-MVP). Pragmatic oscillator synthesis stands in for
// SoundFont until a .sf2/fluidsynth WASM backend is integrated (NFR-9: swappable).

import type { AccompanimentConfig, AccompanimentTimeline, AccompanimentNote } from '../types';

const ROOT_PC: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
const MAJOR_SCALE = [0, 2, 4, 5, 7, 9, 11];

// Roman-numeral degree (I, IV, V...) -> semitone offset within a major key.
function degreeToPc(roman: string, keyPc: number): number {
  const map: Record<string, number> = { I: 0, II: 1, III: 2, IV: 3, V: 4, VI: 5, VII: 6 };
  const deg = map[roman.toUpperCase()];
  if (deg === undefined) return keyPc;
  return (keyPc + MAJOR_SCALE[deg]) % 12;
}

// Parse a chord name like "Am", "C", "G7", "Fmaj7" -> { rootPc, intervals }.
export function parseChord(name: string): { rootPc: number; intervals: number[] } {
  const m = name.match(/^([A-G])(#|b)?(.*)$/);
  if (!m) return { rootPc: 0, intervals: [0, 4, 7] };
  let pc = ROOT_PC[m[1]] ?? 0;
  if (m[2] === '#') pc += 1;
  if (m[2] === 'b') pc -= 1;
  const suffix = m[3] || '';
  let intervals = [0, 4, 7]; // major triad
  if (suffix.startsWith('m') && !suffix.startsWith('maj')) intervals = [0, 3, 7]; // minor
  else if (suffix.startsWith('aug')) intervals = [0, 4, 8];
  else if (suffix.startsWith('dim')) intervals = [0, 3, 6];
  if (suffix.includes('7')) {
    if (suffix.startsWith('maj')) intervals = [0, 4, 7, 11];
    else if (suffix.startsWith('m')) intervals = [0, 3, 7, 10];
    else intervals = [0, 4, 7, 10];
  }
  return { rootPc: ((pc % 12) + 12) % 12, intervals };
}

export function generateAccompaniment(config: AccompanimentConfig): AccompanimentTimeline {
  const keyPc = ROOT_PC[config.key.toUpperCase()] ?? 0;
  // Default progression I-IV-V-I if no chords supplied.
  const chords: { measure: number; pc: number; intervals: number[] }[] =
    config.chords.length > 0
      ? config.chords.map((c) => {
          const parsed = parseChord(c.chordName);
          return { measure: c.measure, pc: parsed.rootPc, intervals: parsed.intervals };
        })
      : [1, 4, 5, 1].map((_, i) => ({ measure: i + 1, pc: degreeToPc(['I', 'IV', 'V', 'I'][i] ?? 'I', keyPc), intervals: [0, 4, 7] }));

  const beatsPerMeasure = config.timeSignature[0];
  const notes: AccompanimentNote[] = [];
  const baseOctave = 4; // MIDI octave for arpeggio register
  chords.forEach((chord, idx) => {
    const startBeat = idx * beatsPerMeasure;
    const ordered = config.pattern === 'up' ? chord.intervals : [...chord.intervals].reverse();
    // Spread arpeggio across the measure, one note per beat (extra beats repeat top).
    for (let b = 0; b < beatsPerMeasure; b++) {
      const interval = ordered[b % ordered.length];
      const midi = (baseOctave + 1) * 12 + ((chord.pc + interval) % 12);
      notes.push({ time: startBeat + b, duration: 1, pitch: midi, velocity: 0.5, part: 'chord' });
    }
  });
  return { notes };
}
